import React, { useState, useCallback, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaLayerGroup, FaCheck } from "react-icons/fa";
import { useCreateWorkspace } from "@/api/account";
import { useQueryClient } from "@tanstack/react-query";
import useTranslation from "@/hooks/use-translation";
import { Button, Input } from "@/components/ui";
import { Typography } from "@/components/ui/typography";
import { Z_INDEX, zIndexStyle } from "@/utils/z-index";
import logger from "@/utils/logger";

interface CreateWorkspaceModalProps {
  /** Whether the modal is open */
  open: boolean;
}

/**
 * Modal displayed when a user has no workspaces.
 * Forces the user to create their first workspace before accessing the app.
 *
 * This modal cannot be closed - the user must create a workspace to proceed.
 */
const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({
  open,
}) => {
  const { t } = useTranslation();
  const [workspaceName, setWorkspaceName] = useState("");
  const [error, setError] = useState<string>();
  const inputRef = useRef<HTMLInputElement>(null);
  
  const createWorkspace = useCreateWorkspace();
  const queryClient = useQueryClient();

  // Focus input when modal opens
  useEffect(() => {
    if (open && inputRef.current) {
      // Delay focus to ensure modal is rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Handle workspace creation
  const handleCreate = useCallback(async () => {
    const trimmedName = workspaceName.trim();
    
    if (!trimmedName) {
      setError(t("workspace_name_required"));
      return;
    }

    setError(undefined);

    try {
      await createWorkspace.mutateAsync({
        name: trimmedName,
      });
      
      // Invalidate workspaces query to refresh the list
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === "string" && key.includes("/workspaces/");
        },
      });
      
      // Reset form
      setWorkspaceName("");
    } catch (err) {
      logger.error("Failed to create workspace:", err);
      setError(t("error500"));
    }
  }, [workspaceName, createWorkspace, queryClient, t]);

  // Handle form submission
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      handleCreate();
    },
    [handleCreate]
  );

  // Handle key press
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleCreate();
      }
    },
    [handleCreate]
  );

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop - no click to close */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md"
            style={zIndexStyle(Z_INDEX.MODAL)}
            aria-hidden="true"
          />

          {/* Modal container */}
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={zIndexStyle(Z_INDEX.MODAL + 1)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 300,
              }}
              className="w-full max-w-md bg-surface-tertiary rounded-arc-2xl shadow-arc-2xl overflow-hidden"
            >
              {/* Header with icon */}
              <div className="px-8 pt-8 pb-4 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    delay: 0.1,
                    damping: 15,
                    stiffness: 200,
                  }}
                  className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-arc-xl bg-accent/10"
                >
                  <FaLayerGroup className="text-3xl text-accent" />
                </motion.div>
                
                <Typography variant="h4" className="font-semibold text-content-primary mb-2">
                  {t("create_first_workspace")}
                </Typography>
                
                <Typography variant="text-sm" className="text-content-secondary">
                  {t("create_workspace_description")}
                </Typography>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="px-8 pb-8">
                <div className="mb-6">
                  <Input
                    ref={inputRef}
                    label={t("workspace_name")}
                    value={workspaceName}
                    onChange={(e) => {
                      setWorkspaceName(e.target.value);
                      if (error) setError(undefined);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="My Workspace"
                    error={error}
                    disabled={createWorkspace.isPending}
                    autoComplete="off"
                  />
                </div>

                {/* Info text about workspaces */}
                <div className="mb-6 p-4 bg-surface-secondary rounded-arc-lg">
                  <Typography variant="text-sm" className="text-content-tertiary">
                    {t("workspaces_description")}
                  </Typography>
                </div>

                {/* Submit button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={createWorkspace.isPending}
                  disabled={!workspaceName.trim() || createWorkspace.isPending}
                  leftIcon={<FaCheck />}
                >
                  {t("create_workspace")}
                </Button>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

CreateWorkspaceModal.displayName = "CreateWorkspaceModal";

export { CreateWorkspaceModal };
export default CreateWorkspaceModal;
